import type {
  BridgeMessage,
  BridgeResponse,
  BridgeInitMessage,
  PluginToHostMessageType,
  FiasTheme,
  PluginPermission,
  EntityInvocationResult,
} from './types';
import { getDefaultTheme } from './theme';

let messageCounter = 0;

function generateMessageId(): string {
  return `msg_${Date.now()}_${++messageCounter}`;
}

/**
 * Merges an incoming theme with defaults to ensure all fields are present.
 * Protects plugins from crashing when the host sends an incomplete theme.
 */
function mergeThemeWithDefaults(incoming: Partial<FiasTheme>): FiasTheme {
  const defaults = getDefaultTheme(incoming.mode ?? 'dark');
  return {
    ...defaults,
    ...incoming,
    colors: { ...defaults.colors, ...(incoming.colors ?? {}) },
    spacing: { ...defaults.spacing, ...(incoming.spacing ?? {}) },
    fonts: { ...defaults.fonts, ...(incoming.fonts ?? {}) },
    components: { ...defaults.components, ...(incoming.components ?? {}) },
  };
}

type PendingRequest = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  onToken?: (text: string) => void;
};

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Low-level bridge client for postMessage communication with the FIAS host frame.
 * Used internally by SDK hooks. Plugin developers should use hooks instead.
 */
export class FiasBridge {
  private pendingRequests = new Map<string, PendingRequest>();
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private initResolve: (() => void) | null = null;
  private archId: string = '';
  private permissions: PluginPermission[] = [];
  private theme: FiasTheme | null = null;
  private currentPath: string = '';
  private hostOrigin: string = '';
  private themeListeners = new Set<(theme: FiasTheme) => void>();
  private navigationListeners = new Set<(path: string) => void>();
  private stepNavigationListeners = new Set<(nodeId: string) => void>();
  private listening = false;

  constructor() {
    this.initPromise = new Promise((resolve) => {
      this.initResolve = resolve;
    });

    this.attach();
  }

  /**
   * Register the message listener. Safe to call multiple times —
   * will not add duplicate listeners.
   */
  attach(): void {
    if (typeof window !== 'undefined' && !this.listening) {
      window.addEventListener('message', this.handleMessage);
      this.listening = true;
    }
  }

  /**
   * Wait for the host to send the init message.
   */
  async waitForInit(): Promise<void> {
    if (this.initialized) return;
    return this.initPromise!;
  }

  /**
   * Signal to the host that the plugin is loaded and ready.
   */
  ready(): void {
    this.send('ready', {});
  }

  /**
   * Request the host to resize the plugin iframe.
   */
  resize(height: number): void {
    this.send('resize', { height });
  }

  /**
   * Show a platform toast notification.
   */
  showToast(message: string, variant: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    this.send('toast', { message, variant });
  }

  /**
   * Send a request to the host and wait for a response.
   */
  async request<T>(
    type: PluginToHostMessageType,
    payload: unknown,
    timeoutMs?: number,
  ): Promise<T> {
    await this.waitForInit();

    return new Promise<T>((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error(`Bridge request timed out: ${type}`));
      }, timeoutMs ?? DEFAULT_TIMEOUT_MS);

      this.pendingRequests.set(messageId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
      });

      this.send(type, payload, messageId);
    });
  }

  getArchId(): string {
    return this.archId;
  }

  getPermissions(): PluginPermission[] {
    return [...this.permissions];
  }

  getTheme(): FiasTheme | null {
    return this.theme;
  }

  getCurrentPath(): string {
    return this.currentPath;
  }

  onThemeUpdate(listener: (theme: FiasTheme) => void): () => void {
    this.themeListeners.add(listener);
    return () => this.themeListeners.delete(listener);
  }

  onNavigationUpdate(listener: (path: string) => void): () => void {
    this.navigationListeners.add(listener);
    return () => this.navigationListeners.delete(listener);
  }

  onStepNavigationUpdate(listener: (nodeId: string) => void): () => void {
    this.stepNavigationListeners.add(listener);
    return () => this.stepNavigationListeners.delete(listener);
  }

  /**
   * Send a streaming entity_invoke request. Tokens arrive via stream_token
   * messages and are forwarded to the onToken callback. The returned promise
   * resolves with the final EntityInvocationResult when the host sends the
   * normal response message.
   */
  async requestStream(
    payload: unknown,
    onToken: (text: string) => void,
  ): Promise<EntityInvocationResult> {
    await this.waitForInit();

    return new Promise<EntityInvocationResult>((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error('Bridge streaming request timed out: entity_invoke'));
      }, 120_000); // Longer timeout for streaming

      this.pendingRequests.set(messageId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
        onToken,
      });

      this.send('entity_invoke', { ...(payload as object), stream: true }, messageId);
    });
  }

  /**
   * Clean up event listeners and pending requests.
   * After destroy(), the bridge can be re-activated by calling attach().
   */
  destroy(): void {
    if (typeof window !== 'undefined' && this.listening) {
      window.removeEventListener('message', this.handleMessage);
      this.listening = false;
    }
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timer);
      pending.reject(new Error('Bridge destroyed'));
    }
    this.pendingRequests.clear();
    this.themeListeners.clear();
    this.navigationListeners.clear();
    this.stepNavigationListeners.clear();
  }

  private handleMessage = (event: MessageEvent): void => {
    const data = event.data;
    if (!data || typeof data !== 'object' || !data.type) return;

    // Handle init message from host (only process once to prevent replay).
    // The first init message establishes the trusted host origin.
    if (data.type === 'init') {
      if (this.initialized) return;
      this.hostOrigin = event.origin;
      const initMsg = data as BridgeInitMessage;
      this.archId = initMsg.payload.archId;
      this.permissions = initMsg.payload.permissions;
      this.theme = mergeThemeWithDefaults(initMsg.payload.theme);
      this.currentPath = initMsg.payload.currentPath;
      this.initialized = true;
      this.initResolve?.();
      return;
    }

    // After init, only accept messages from the verified host origin
    if (this.hostOrigin && event.origin !== this.hostOrigin) return;

    // Handle theme updates
    if (data.type === 'theme_update') {
      this.theme = mergeThemeWithDefaults(data.payload as Partial<FiasTheme>);
      for (const listener of this.themeListeners) {
        listener(this.theme);
      }
      return;
    }

    // Handle navigation updates
    if (data.type === 'navigate_update') {
      this.currentPath = data.payload as string;
      for (const listener of this.navigationListeners) {
        listener(this.currentPath);
      }
      return;
    }

    // Handle step navigation from host
    if (data.type === 'step_navigate') {
      const { nodeId } = data.payload as { nodeId: string };
      if (typeof nodeId === 'string') {
        for (const listener of this.stepNavigationListeners) {
          listener(nodeId);
        }
      }
      return;
    }

    // Handle streaming tokens
    if (data.type === 'stream_token') {
      const { messageId, text } = data as { type: string; messageId: string; text: string };
      const pending = this.pendingRequests.get(messageId);
      if (pending?.onToken && typeof text === 'string') {
        pending.onToken(text);
      }
      return;
    }

    // Handle response messages
    if (data.type === 'response') {
      const response = data as BridgeResponse;
      const pending = this.pendingRequests.get(response.messageId);
      if (!pending) return;

      clearTimeout(pending.timer);
      this.pendingRequests.delete(response.messageId);

      if (response.error) {
        pending.reject(new Error(response.error));
      } else {
        pending.resolve(response.payload);
      }
    }
  };

  private send(type: PluginToHostMessageType, payload: unknown, messageId?: string): void {
    if (typeof window === 'undefined') return;

    const message: BridgeMessage = {
      type,
      messageId: messageId ?? generateMessageId(),
      payload,
    };

    // Use the verified host origin when available; fall back to '*' only
    // for the initial 'ready' message sent before init is received.
    const targetOrigin = this.hostOrigin || '*';
    window.parent.postMessage(message, targetOrigin);
  }
}

/**
 * Singleton bridge instance. Eagerly created at module scope so the
 * message listener is registered synchronously during import, before
 * the iframe `load` event fires and the host sends the init message.
 */
let bridgeInstance: FiasBridge | null = typeof window !== 'undefined' ? new FiasBridge() : null;

export function getBridge(): FiasBridge {
  if (!bridgeInstance) {
    bridgeInstance = new FiasBridge();
  }
  return bridgeInstance;
}

/**
 * Reset the bridge singleton (used in testing).
 */
export function resetBridge(): void {
  bridgeInstance?.destroy();
  bridgeInstance = null;
}
